import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Users, 
  BookOpen, 
  Certificate,
  ChartLineUp,
  TrendUp,
  Trophy,
  Spinner,
  UserPlus,
  SignIn,
  Globe,
  ClockCounterClockwise
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [courseReports, setCourseReports] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, coursesRes, activityRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/overview`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/by-course`, { withCredentials: true }),
        axios.get(`${API_URL}/api/activity-logs?limit=5`, { withCredentials: true })
      ]);
      setOverview(overviewRes.data);
      setCourseReports(coursesRes.data);
      setRecentActivity(activityRes.data.logs || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#2C3E50', '#27AE60', '#F39C12', '#E74C3C'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  const pieData = courseReports.map(course => ({
    name: course.course_title.length > 20 ? course.course_title.substring(0, 20) + '...' : course.course_title,
    value: course.enrollments
  })).filter(d => d.value > 0);

  return (
    <DashboardLayout>
      <div data-testid="admin-dashboard">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Painel de Controle</h1>
        <p className="text-[#5C6B7A] mb-8">Visão geral da sua plataforma de treinamento</p>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-[#2C3E50]/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#5C6B7A]">Total de Usuários</CardTitle>
              <Users size={20} className="text-[#2C3E50]" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-[#1A252F]" data-testid="total-users">
                {overview?.total_users || 0}
              </p>
              <p className="text-xs text-[#27AE60] flex items-center gap-1 mt-1">
                <TrendUp size={14} />
                Clientes ativos
              </p>
            </CardContent>
          </Card>

          <Card className="border-[#2C3E50]/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#5C6B7A]">Total de Cursos</CardTitle>
              <BookOpen size={20} className="text-[#2C3E50]" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-[#1A252F]" data-testid="total-courses">
                {overview?.total_courses || 0}
              </p>
              <p className="text-xs text-[#5C6B7A] mt-1">Cursos publicados</p>
            </CardContent>
          </Card>

          <Card className="border-[#2C3E50]/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#5C6B7A]">Taxa de Conclusão</CardTitle>
              <ChartLineUp size={20} className="text-[#27AE60]" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-[#27AE60]" data-testid="completion-rate">
                {overview?.completion_rate?.toFixed(1) || 0}%
              </p>
              <p className="text-xs text-[#5C6B7A] mt-1">
                {overview?.completed_courses || 0} de {overview?.total_enrollments || 0} matrículas
              </p>
            </CardContent>
          </Card>

          <Card className="border-[#2C3E50]/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#5C6B7A]">Média dos Quizzes</CardTitle>
              <Trophy size={20} className="text-[#F39C12]" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-[#F39C12]" data-testid="avg-score">
                {overview?.average_quiz_score?.toFixed(1) || 0}%
              </p>
              <p className="text-xs text-[#5C6B7A] mt-1">Em todos os quizzes</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-[#2C3E50]/10">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1A252F]">Matrículas por Curso</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={courseReports}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#BDC3C7" />
                    <XAxis 
                      dataKey="course_title" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="enrollments" fill="#2C3E50" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#2C3E50]/10">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1A252F]">Distribuição de Cursos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-[#5C6B7A]">
                    Sem dados de matrícula ainda
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="border-[#2C3E50]/10 mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#1A252F]">Atividades Recentes</CardTitle>
            <Link to="/admin/activity" className="text-sm text-[#2980B9] hover:underline font-medium" data-testid="view-all-activity">
              Ver todas
            </Link>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-[#5C6B7A] text-center py-4">Nenhuma atividade registrada ainda</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((log, i) => {
                  const iconMap = {
                    user_registered: { Icon: UserPlus, color: '#27AE60' },
                    user_login: { Icon: SignIn, color: '#2980B9' },
                    enrollment: { Icon: BookOpen, color: '#8E44AD' },
                    certificate_generated: { Icon: Certificate, color: '#F39C12' },
                    domain_added: { Icon: Globe, color: '#2980B9' },
                    domain_removed: { Icon: Globe, color: '#E74C3C' },
                  };
                  const { Icon, color } = iconMap[log.action] || { Icon: ClockCounterClockwise, color: '#5C6B7A' };
                  const timeAgo = (() => {
                    const diff = Math.floor((Date.now() - new Date(log.created_at)) / 60000);
                    if (diff < 1) return 'Agora';
                    if (diff < 60) return `${diff}min`;
                    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
                    return `${Math.floor(diff / 1440)}d`;
                  })();
                  return (
                    <div key={i} className="flex items-center gap-3" data-testid={`recent-activity-${i}`}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '15' }}>
                        <Icon size={16} style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#1A252F] truncate">
                          <span className="font-medium">{log.user_name}</span>
                          {log.details && <span className="text-[#5C6B7A]"> — {log.details}</span>}
                        </p>
                      </div>
                      <span className="text-xs text-[#95A5A6] flex-shrink-0">{timeAgo}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/admin/courses" className="block">
            <Card className="border-[#2C3E50]/10 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#2C3E50] flex items-center justify-center">
                  <BookOpen size={24} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A252F]">Gerenciar Cursos</p>
                  <p className="text-sm text-[#5C6B7A]">Criar e editar cursos</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/users" className="block">
            <Card className="border-[#2C3E50]/10 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#27AE60] flex items-center justify-center">
                  <Users size={24} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A252F]">Gerenciar Usuários</p>
                  <p className="text-sm text-[#5C6B7A]">Ver e gerenciar usuários</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/reports" className="block">
            <Card className="border-[#2C3E50]/10 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#F39C12] flex items-center justify-center">
                  <ChartLineUp size={24} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A252F]">Ver Relatórios</p>
                  <p className="text-sm text-[#5C6B7A]">Análises detalhadas</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminDashboard;
